import React from 'react';

export function SortIcon({ active, direction }) {
  if (!active) {
    return (
      <svg className="inline h-3 w-3 ml-1 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path d="M8 9l4-4 4 4M8 15l4 4 4-4" />
      </svg>
    );
  }
  return direction === 'asc' ? (
    <svg className="inline h-3 w-3 ml-1 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path d="M8 15l4-4 4 4" />
    </svg>
  ) : (
    <svg className="inline h-3 w-3 ml-1 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path d="M8 9l4 4 4-4" />
    </svg>
  );
}

export function SortableTh({ sortKey, sortConfig, onSort, children, className = '' }) {
  const active = sortConfig?.key === sortKey;
  return (
    <th
      className={`cursor-pointer select-none hover:bg-slate-100 transition-colors ${className}`}
      onClick={() => onSort(sortKey)}
    >
      {children}
      <SortIcon active={active} direction={sortConfig?.direction} />
    </th>
  );
}

/**
 * Seitenblätterung. Standardmäßig die Seiten 1..totalPages; wird `pages` als
 * explizite, geordnete Liste von Seitennummern übergeben, blättert die Leiste
 * darüber – so sind auch Folgen mit Lücke oder negativen Nummern möglich
 * (Kalenderliste: vergangene Einträge auf negativen Seiten).
 */
export function Pagination({ currentPage, totalPages, onPageChange, className = '', pages }) {
  const allPages = pages ?? Array.from({ length: totalPages }, (_, i) => i + 1);
  if (allPages.length <= 1) return null;
  const idx = Math.max(0, allPages.indexOf(currentPage));
  const start = Math.max(0, idx - 2);
  const end = Math.min(allPages.length - 1, idx + 2);
  const fenster = allPages.slice(start, end + 1);
  const atStart = idx === 0;
  const atEnd = idx === allPages.length - 1;
  return (
    <div className={`flex items-center justify-center flex-wrap gap-1 ${className}`}>
      <button className="btn-secondary btn-sm btn" onClick={() => onPageChange(allPages[0])} disabled={atStart}>«</button>
      <button className="btn-secondary btn-sm btn" onClick={() => onPageChange(allPages[idx - 1])} disabled={atStart}>‹</button>
      {fenster.map((p) => (
        <button
          key={p}
          onClick={() => onPageChange(p)}
          className={`btn btn-sm ${p === currentPage ? 'btn-primary' : 'btn-secondary'}`}
        >
          {p}
        </button>
      ))}
      <button className="btn-secondary btn-sm btn" onClick={() => onPageChange(allPages[idx + 1])} disabled={atEnd}>›</button>
      <button className="btn-secondary btn-sm btn" onClick={() => onPageChange(allPages[allPages.length - 1])} disabled={atEnd}>»</button>
    </div>
  );
}

export function ActionBtn({ onClick, title, variant = 'ghost', children, disabled, as: Tag = 'button', to, ...rest }) {
  const colors = {
    ghost: 'text-slate-500 hover:bg-slate-100 hover:text-slate-700',
    info: 'text-sky-600 hover:bg-sky-50',
    warning: 'text-amber-600 hover:bg-amber-50',
    danger: 'text-red-600 hover:bg-red-50',
    success: 'text-emerald-600 hover:bg-emerald-50',
    primary: 'text-brand-600 hover:bg-brand-50',
  };
  const cls = `inline-flex items-center justify-center rounded-lg p-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${colors[variant] ?? colors.ghost}`;

  if (Tag !== 'button') {
    return (
      <Tag to={to} className={cls} title={title} {...rest}>
        {children}
      </Tag>
    );
  }
  return (
    <button className={cls} onClick={onClick} title={title} disabled={disabled} type="button" {...rest}>
      {children}
    </button>
  );
}

export function EyeIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
export function ClipboardListIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
    </svg>
  );
}
export function PencilIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}
export function TrashIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  );
}
export function PlusIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}
export function SearchIcon() {
  return (
    <svg className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}
export function ArrowLeftIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
    </svg>
  );
}
export function ArrowUpIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <line x1="12" y1="19" x2="12" y2="5" /><polyline points="5 12 12 5 19 12" />
    </svg>
  );
}
export function ArrowDownIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <line x1="12" y1="5" x2="12" y2="19" /><polyline points="19 12 12 19 5 12" />
    </svg>
  );
}
export function ChevronRightIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}
export function KeyIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
    </svg>
  );
}
export function MapPinIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path d="M21 10c0 6.5-9 12-9 12s-9-5.5-9-12a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}
export function LockIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}
export function UnlockIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 7.58-1.73" />
    </svg>
  );
}
